import { stringify } from "query-string";
import { handleError, EVENT_TYPE } from "./helpers";

const CREDS = { credentials: "same-origin" };

const MAIN_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
  "Cache-Control": "no-cache"
};

const ALL_HEADERS = {
  Accept:
    "text/plain,application/zip,application/x-zip-compressed,application/octet-stream,application/json",
  "Content-Type": "application/json"
};

const FETCH_HEADERS = {
  headers: { ...MAIN_HEADERS },
  ...CREDS
};

const csrfToken = (() => {
  const meta = document.head.querySelector("meta[name='csrf-token']");

  return meta && meta.getAttribute("content");
})();

const AUTH = {
  "X-Requested-With": "XMLHttpRequest",
  "X-CSRF-Token": csrfToken
};

const POST_HEADERS = {
  headers: {
    ...MAIN_HEADERS,
    ...AUTH
  },
  ...CREDS
};

const ALL_POST_HEADERS = {
  headers: {
    ...ALL_HEADERS,
    ...AUTH
  },
  ...CREDS
};

const REQUEST_TYPE = {
  POST: "POST",
  GET: "GET",
  DELETE: "DELETE",
  PUT: "PUT",
  PATCH: "PATCH"
};

/**
 * Utils
 */

const getUrl = (url) => {
  // Node fetch in test(node) env needs absolute urls
  if (process.env.NODE_ENV == "test" && url[0] == "/") {
    return `https://manta.local.mt.d.demystdata.com:8443/${url}`;
  }
  return url;
};

class ServerError extends Error {
  constructor(response, body) {
    super();
    this.response = response;
    this.body = body;

    let message;
    if (body && body.response && body.response.message) {
      console.log("Found error message(s)");
      ({ message } = body.response);
    }
    this.message = message;
  }
}

const catchServerErrors = async (res, mime) => {
  if (!mime) {
    console.log("No type provided. Defaulting to JSON");
    mime = "json";
  }

  if (res.ok) {
    if (mime === "*") {
      // This signifies we have no idea what the client will send ahead of time
      return res;
    }
    return res.clone()[mime](); // json, text etc
  }

  let body = null;
  const resClone = res.clone();
  try {
    body = await res.json();
  } catch (error) {
    body = await resClone.text();
  }

  if (Array.isArray(body)) {
    body = body.join(", ");
  }

  throw new ServerError(res, body);
};

export const makeRequest = ({
  url,
  type,
  fetchOptions = {
    method: REQUEST_TYPE.GET,
    ...FETCH_HEADERS
  },
  mime = "json",
  shouldLogError = true
}) => new Promise((resolve, reject) => {
  const defaultErrorMessage = `Failed to ${type}`;

  fetch(getUrl(url), fetchOptions)
    .then(res => catchServerErrors(res, mime))
    .then((res) => {
      const error = res && res.errors && `${defaultErrorMessage}. ${res.errors.join(", ")}`;
      if (error) {
        reject(new Error(error));
        return;
      }
      resolve(res);
    })
    .catch((error) => {
      if (error.response && error.response.status == 401) {
        // Redirect to login page, unless we're already on it
        if (url !== "/jwt/user_data" && !window.location.pathname.startsWith("/users")) {
          window.location.pathname = "/users/sign_in";
        }
      }

      if (shouldLogError) {
        // Log request error, unless it's logging the error that has caused an error
        handleError(
          defaultErrorMessage,
          error,
          EVENT_TYPE.INVALID_FETCH_REQUEST,
          {
            url,
            fetchOptions,
            statusCode: error.response && error.response.status
          }
        );
      }

      reject(error);
    });
});

export const makeLogRequest = args => makeRequest({
  ...args,
  shouldLogError: false
});

export const createPipes = creationPromises => Promise.all(creationPromises);

/**
 * PUT/PATCH Actions
 */

export const updateCurrentOrganization = data => makeRequest({
  url: "/organization",
  type: "update current organization",
  fetchOptions: {
    method: REQUEST_TYPE.PATCH,
    ...POST_HEADERS,
    body: JSON.stringify({ organization: data })
  }
});

export const updateUser = (user_id, user) => makeRequest({
  url: `/organization/users/${user_id}`,
  type: "update user",
  fetchOptions: {
    method: REQUEST_TYPE.PATCH,
    ...POST_HEADERS,
    body: JSON.stringify({ user })
  }
});

export const updateUserRole = (user_id, role_id) => makeRequest({
  url: `/organization/users/${user_id}`,
  type: "update user's role",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({
      user: {
        role_id
      }
    })
  }
});

export const updateUserStatus = (user_id, active) => makeRequest({
  url: `/organization/users/${user_id}`,
  type: "set user status",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({
      user: {
        active
      }
    })
  }
});

export const sendResetPasswordInstructions = (email_address, captchaResponse) => makeRequest({
  url: "/users/password",
  type: "request password reset instructions",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      user: {
        email: email_address
      },
      g_recaptcha_response: captchaResponse
    })
  }
});

export const resendUnlockInstructions = (email_address, captchaResponse) => makeRequest({
  url: "/users/unlock",
  type: "request password reset instructions",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      user: {
        email: email_address
      },
      g_recaptcha_response: captchaResponse
    })
  }
});

export const resendConfirmationInstructions = (email_address, captchaResponse) => makeRequest({
  url: "/users/confirmation",
  type: "request confirmation instructions",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      user: {
        email: email_address
      },
      g_recaptcha_response: captchaResponse
    })
  }
});

export const updatePassword = (current_password, password, password_confirmation) => makeRequest({
  url: "/users.json",
  type: "change user password",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({
      user: {
        current_password,
        password,
        password_confirmation
      }
    })
  }
});

export const resetPassword = (user, captchaResponse) => makeRequest({
  url: "/users/password",
  type: "change user password",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({
      user,
      g_recaptcha_response: captchaResponse
    })
  }
});

export const updateCustomCategory = (id, data) => makeRequest({
  url: `/custom_categories/${id}`,
  type: "update custom_category",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({ custom_category: data })
  }
});

export const followBatch = id => makeRequest({
  url: `/aegean_batch_runs/${id}/follow`,
  type: "follow aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const unfollowBatch = id => makeRequest({
  url: `/aegean_batch_runs/${id}/unfollow`,
  type: "unfollow aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const deleteBatchRun = id => makeRequest({
  url: "/aegean_batch_runs/archive_all",
  type: `archive aegean_batch_run ${id}`,
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_run_ids: [id] })
  }
});

export const archiveAllBatchRuns = ids => makeRequest({
  url: "/aegean_batch_runs/archive_all",
  type: "archive aegean_batch_runs",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_run_ids: ids })
  }
});

export const updateWorkspace = (id, data) => makeRequest({
  url: `/workspaces/${id}`,
  type: "update workspace",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({ workspace: data })
  }
});

export const updateBatchRun = (id, data) => makeRequest({
  url: `/aegean_batch_runs/${id}`,
  type: "update aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_run: data })
  }
});

export const updateBatchNote = (id, data) => makeRequest({
  url: `/aegean_batch_runs/${id}/update_note`,
  type: "update_note aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ note: data })
  }
});

export const pauseBatchRun = id => makeRequest({
  url: `/aegean_batch_runs/${id}/pause`,
  type: "pause aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const continueBatchRun = id => makeRequest({
  url: `/aegean_batch_runs/${id}/continue`,
  type: "continue aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const approveBatchRun = id => makeRequest({
  url: `/aegean_batch_runs/${id}/approve`,
  type: "approve aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const cancelBatchRun = id => makeRequest({
  url: `/aegean_batch_runs/${id}/cancel`,
  type: "cancel aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const updateBatchInput = (id, data) => makeRequest({
  url: `/aegean_batch_inputs/${id}`,
  type: "update aegean_batch_input",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_input: data })
  }
});

export const updateChannel = (id, data) => makeRequest({
  url: `/channels/${id}`,
  type: "update channel",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    ...POST_HEADERS,
    body: JSON.stringify({
      channel: data,
      front_end: true
    })
  }
});

export const updatePipe = (id, data) => makeRequest({
  url: `/pipes/${id}`,
  type: "update pipe",
  fetchOptions: {
    method: REQUEST_TYPE.PATCH,
    ...POST_HEADERS,
    body: JSON.stringify({
      pipe: data,
      front_end: true
    })
  }
});

export const createS3BatchInputFile = (url, data) => makeRequest({
  url,
  type: "create s3 batch input file",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    body: data
  },
  mime: "*"
});

export const createS3BatchExportFile = (url, data, headers = {}) => makeRequest({
  url,
  type:
    "create s3 batch input file",
  fetchOptions: {
    method: REQUEST_TYPE.PUT,
    body: data,
    headers
  },
  mime: "*"
});

/**
 *  POST Actions
 */

export const grantOrganizationCredits = amount => makeRequest({
  url: "/credits",
  type: "add more credits to organization",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      credit: { amount }
    })
  }
});

export const organizationAcceptUser = userId => makeRequest({
  url: `/organization/users/${userId}/accept`,
  type: "accept user invited to organization",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const organizationRejectUser = userId => makeRequest({
  url: `/organization/users/${userId}/reject`,
  type: "reject user invited to organization",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

export const organizationUnlockUser = userId => makeRequest({
  url: `/organization/users/${userId}/unlock`,
  type: "unlock user invited to organization",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS
  }
});

// TODO: Add controller test to see if users can send up a role id not in their org
export const organizationInviteUser = ({ email, role_id }) => makeRequest({
  url: "/users/invitation",
  type: "invite user to organization",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      user: {
        email,
        role_id
      }
    })
  }
});

export const createWhitelistedIpAddress = (organization_id, address) => makeRequest({
  url: "/ip_whitelists",
  type: "create whitelisted ip address",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      organization_id,
      address
    })
  }
});

export const createDataProviderCredential = (credential_key_id, value) => makeRequest({
  url: "/credentials",
  type: "create data provider credentials",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      aegean: "aegean",
      credential: {
        credential_key_id,
        value
      }
    })
  }
});

export const createProductionApiKey = data => makeRequest({
  url: "/api_keys",
  type: "create a production api key",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      ...data,
      v1: true
    })
  }
});

export const createTestApiKey = () => makeRequest({
  url: "/test_api_keys",
  type: "create a test api key",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      aegean: "aegean",
      v1: true
    })
  }
});

export const queryBlackfin = (blackfin, data) => makeRequest({
  url: `${blackfin}/v2/execute`,
  type: "query blackfin",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    body: JSON.stringify(data)
  }
});

export const createBookmark = data => makeRequest({
  url: "/bookmarks",
  type: "create bookmark",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ bookmark: data })
  }
});

export const createCustomCategory = data => makeRequest({
  url: "/custom_categories",
  type: "create custom category",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ custom_category: data })
  }
});

export const assignOfflineProvider = (id, data) => makeRequest({
  url: `/batch_run_provider_versions/${id}/assign`,
  type:
    "assign offline provider",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ batch_run_provider_version: data })
  }
});

export const updateWorkspaceProviderNote = (id, providerId, note) => makeRequest({
  url: `/workspaces/${id}/update_provider_note`,
  type:
    "update workspace note",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      id,
      provider_id: providerId,
      note
    })
  }
});

export const replaceUsersInWorkspace = (id, data) => makeRequest({
  url: `/workspaces/${id}/replace_users_in_workspace`,
  type: "replace workspace users",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ workspace: data })
  }
});

export const createUser = (data, captchaResponse) => {
  const { organization, ...user } = data;

  return makeRequest({
    url: "/users",
    type: "create user",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify({
        user,
        organization,
        g_recaptcha_response: captchaResponse
      })
    },
    mime: "*"
  });
};

export const addProvidersToWorkspace = (id, data) => makeRequest({
  url: "/workspaces/add_providers_to_workspace",
  type:
    "add workspace_providers",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      id,
      workspace: data
    })
  }
});

export const createBatchExport = data => makeRequest({
  url: "/aegean_batch_exports",
  type: "create aegean_batch_export",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_export: data })
  }
});

export const createOfflineBatchExport = data => makeRequest({
  url: "/aegean_batch_exports/create_offline",
  type:
    "create aegean_batch_export",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_export: data })
  }
});

export const createBatchRun = (data, error_type) => makeRequest({
  url: "/aegean_batch_runs",
  type: "create aegean_batch_run",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_run: data })
  },
  errorType: error_type,
  mime: "json"
});

export const createBatchInput = data => makeRequest({
  url: "/aegean_batch_inputs",
  type: "create aegean_batch_input",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean_batch_input: data })
  }
});

export const createChannel = data => makeRequest({
  url: "/channels",
  type: "create channel",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      channel: data,
      front_end: true
    })
  }
});

export const createPipe = data => makeRequest({
  url: "/pipes",
  type: "create pipe",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      pipe: data,
      front_end: true
    })
  }
});

export const createDataFunction = data => makeRequest({
  url: "/data_functions",
  type:
    "create data_function",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...ALL_POST_HEADERS,
    body: JSON.stringify(data)
  },
  mime: "*"
});

export const createWorkspace = (newWorkspaceData, useCaseId = null) => makeRequest({
  url: "/workspaces",
  type: "create workspace",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      workspace: newWorkspaceData,
      front_end: true,
      use_case_id: useCaseId
    })
  }
});

export const associatePipeWithDf = (data) => {
  console.log("associating pipe with datafunction - calling api with: ", data);
  return makeRequest({
    url: "/data_functions/add_data_function_to_pipe",
    type:
    "associate pipe_data_function",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify(data)
    }
  });
};

export const updatePipeWithDataFunction = (data) => {
  console.log("updating pipe's df: ", data);
  return makeRequest({
    url: "/data_functions/add_data_function_to_pipe",
    type:
    "update pipe_data_function",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify(data)
    }
  });
};

export const getSampleData = providerId => makeRequest({
  url: `/table_providers/${providerId}/download_sample_data`,
  type:
    "download_table_provider_sample_data",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ table_provider: { id: providerId } })
  }
});

export const logData = (type, event, data) =>
  // Logs either error, info, or warn to manta
  makeLogRequest({
    url: "/log",
    type: "log ui_data",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify({
        log: {
          type,
          event,
          data
        }
      })
    },
    log: false
  });

export const requestWorkspaceTemplate = newTemplateName => makeRequest({
  url: "/request_new_resource",
  type: "request workspace_template",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ workspace_template: { name: newTemplateName } })
  }
});

export const requestSampleData = newProvider => makeRequest({
  url: "/request_new_resource",
  type: "request sample_data",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ sample_data: { provider_name: newProvider } })
  }
});

export const requestDataVendor = (dataVendor) => {
  if (!dataVendor || !Object.keys(dataVendor).length) {
    return handleError(
      "Failed requesting new data vendor",
      new Error("dataVendor is not defined"),
      EVENT_TYPE.ERROR.NEW_DATA_REQUEST_FETCH,
      {},
      "warn"
    );
  }
  return makeRequest({
    url: "/request_new_resource",
    type: "request data_vendor",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify({ data_vendor: dataVendor })
    }
  });
};

export const requestProvider = (newProvider) => {
  if (!newProvider || !Object.keys(newProvider).length) {
    return handleError(
      "Failed requesting new provider",
      new Error("newProvider is not defined"),
      EVENT_TYPE.ERROR.NEW_DATA_REQUEST_FETCH,
      {},
      "warn"
    );
  }
  return makeRequest({
    url: "/request_new_resource",
    type: "new provider_request",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify({ provider: { name: newProvider } })
    }
  });
};

export const requestOfflineProvider = (newOfflineProvider) => {
  if (!newOfflineProvider || !Object.keys(newOfflineProvider).length) {
    return handleError(
      "Failed requesting new offline append only provider",
      new Error("newOfflineProvider is not defined"),
      EVENT_TYPE.ERROR.NEW_DATA_REQUEST_FETCH,
      {},
      "warn"
    );
  }
  return makeRequest({
    url: "/request_new_resource",
    type:
    "request offline_provider",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify({ offline_provider: { name: newOfflineProvider } })
    }
  });
};

export const requestSubscriptionUpgrade = (newPlan) => {
  if (!newPlan || !Object.keys(newPlan).length) {
    return handleError(
      "Failed requesting new subscription upgrade",
      new Error("newPlan is not defined"),
      EVENT_TYPE.ERROR.NEW_DATA_REQUEST_FETCH,
      {},
      "warn"
    );
  }
  return makeRequest({
    url: "/request_new_resource",
    type:
    "request subscription_upgrade",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify({ subscription_upgrade: { name: newPlan } })
    }
  });
};

export const requestNonApprovedProvider = (newNonApprovedProvider) => {
  if (!newNonApprovedProvider || !Object.keys(newNonApprovedProvider).length) {
    return handleError(
      "Failed requesting new non approved provider",
      new Error("newNonApprovedProvider is not defined"),
      EVENT_TYPE.ERROR.NEW_DATA_REQUEST_FETCH,
      {},
      "warn"
    );
  }
  return makeRequest({
    url: "/request_new_resource",
    type:
    "request non_approved_provider",
    fetchOptions: {
      method: REQUEST_TYPE.POST,
      ...POST_HEADERS,
      body: JSON.stringify({ non_approved_provider: { name: newNonApprovedProvider } })
    }
  });
};

export const inputScan = id => makeRequest({
  url: "/aegean_batch_inputs/scan",
  type: "delete aegean_batch_input",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({ id })
  }
});

/**
 * DELETE Actions
 */

export const deleteWhitelistedIpAddress = id => makeRequest({
  url: `/ip_whitelists/${id}`,
  type: "delete whitelisted ip address",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS
  }
});

export const deleteDataProviderCredential = id => makeRequest({
  url: `/credentials/${id}`,
  type: "delete data provider credentials",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS,
    body: JSON.stringify({
      aegean: "aegean",
      v1: true
    })
  }
});

export const deleteProductionApiKey = id => makeRequest({
  url: `/api_keys/${id}`,
  type: "delete production api key",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS,
    body: JSON.stringify({
      aegean: "aegean",
      v1: true
    })
  }
});

export const deleteTestApiKey = id => makeRequest({
  url: `/test_api_keys/${id}`,
  type: "delete test api key",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS,
    body: JSON.stringify({ aegean: "aegean" })
  }
});


export const deleteBookmark = id => makeRequest({
  url: `/bookmarks/${id}`,
  type: "delete bookmark",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS
  }
});

export const deleteCustomCategory = id => makeRequest({
  url: `/custom_categories/${id}`,
  type: "delete custom_categories",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS
  }
});

export const deleteProvidersFromWorkspace = (id, providerIds) => makeRequest({
  url: `/workspaces/${id}/delete_providers`,
  type:
    "delete workspace providers",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS,
    body: JSON.stringify({ workspace: { provider_version_ids: providerIds } })
  }
});

export const deleteChannel = id => makeRequest({
  url: `/channels/${id}`,
  type: "delete channel",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS,
    body: JSON.stringify({ front_end: true })
  }
});

export const deletePipe = id => makeRequest({
  url: `/pipes/${id}`,
  type: "delete pipe",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS,
    body: JSON.stringify({ front_end: true })
  }
});

export const deleteWorkspace = id => makeRequest({
  url: `/workspaces/${id}`,
  type: "delete workspace",
  fetchOptions: {
    method: REQUEST_TYPE.DELETE,
    ...POST_HEADERS
  }
});

export const getSignInInfo = () => makeRequest({
  url: "/captcha_sign_in",
  type: "get sign in information"
});

export const getSignUpInfo = () => makeRequest({
  url: "/captcha_sign_up",
  type: "get sign up information"
});

export const getResetPasswordInfo = () => makeRequest({
  url: "/captcha_reset_password",
  type: "get reset password information"
});

export const getSendResetPasswordInstructionsInfo = () => makeRequest({
  url: "/captcha_send_reset_password_instructions",
  type: "get reset password information"
});

export const getResendUnlockInstructionsInfo = () => makeRequest({
  url: "/captcha_resend_unlock_instructions",
  type: "get resend unlock information"
});

export const getResendConfirmationInstructionsInfo = () => makeRequest({
  url: "/captcha_resend_confirmation_instructions",
  type: "get resend confirmation information"
});

export const getSession = () => makeRequest({
  url: "/jwt/user_data",
  type: "get session information",
  shouldLogError: false // Returns an error when the user is not logged in
});

export const logIn = (user, captchaResponse) => makeRequest({
  url: "/users/sign_in",
  type: "sign in",
  fetchOptions: {
    method: REQUEST_TYPE.POST,
    ...POST_HEADERS,
    body: JSON.stringify({
      user,
      g_recaptcha_response: captchaResponse
    })
  }
});

export const logOut = async () => {
  const logoutUrl = getUrl("/users/sign_out");
  const encodedCSRFToken = encodeURIComponent(csrfToken);

  // Can't get it to work with JSON Content-Type, oh well
  const logoutFetchOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded"
    },
    body: `_method=delete&authenticity_token=${encodedCSRFToken}`
  };


  try {
    const logoutFetchResult = await fetch(logoutUrl, logoutFetchOptions);
    if (logoutFetchResult.status == 200) {
      const myJson = await logoutFetchResult.json();
      if (myJson.next_step) {
        window.location = myJson.next_step;
      }
      return myJson;
    } else {
      return logoutFetchResult;
    }
  } catch (err) {
    console.log("New logout err", err);
    return false;
  }
};


/**
 * GET Actions
 */

export const getPublicSessionHash = () => makeRequest({
  url: "/session_hash",
  type: "get session hash for all users"
});

export const getJwt = () => makeRequest({
  url: "/jwt/show",
  type: "getjwt for logged in user"
});

export const getUserSessionData = () => makeRequest({
  url: "/jwt/user_data",
  type: "get user's session data"
});

export const getUseCases = () => makeRequest({
  url: "/use_cases",
  type: "get list of use cases"
});

export const getWhitelistedIpAddresses = () => makeRequest({
  url: "/ip_whitelists",
  type: "get list of whitelisted ip addresses"
});

export const getAllOrganizations = () => makeRequest({
  url: "/organization/get_all_organizations",
  type: "get all organizations"
});

export const getOrganizationRoles = () => makeRequest({
  url: "/organization/accessible_roles",
  type: "get organization's roles"
});

export const getCurrentOrganizationUsers = () => makeRequest({
  url: "/manageable_users",
  type: "get organization users to manage in admin settings",
  // returns a 403 if the user doesn't have the correct permissions. ideally, we'd stop making this
  // request in those cases, but we make it in a lot of places, and i don't want to make expansive
  // changes to stingray. but also, don't wanna keep seeing this error in the logs for an expected
  // 403 request.
  shouldLogError: false
});

export const getOrganizationUsers = id => makeRequest({
  url: `/users_within_organization?organization_id=${id}`,
  type: "get certain organization users"
});

export const getDataProviderCredentials = () => makeRequest({
  url: "/credentials",
  type: "get data provider credentials"
});

export const getProductionApiKeys = () => makeRequest({
  url: "/api_keys?v1=true",
  type: "get production api keys"
});

export const getTestApiKeys = () => makeRequest({
  url: "/test_api_keys?v1=true",
  type: "get test api keys"
});

export const getEnvVariables = () => makeRequest({
  url: "/get_env_variables",
  type: "get rails environment variables"
});

export const getBookmarks = () => makeRequest({
  url: "/bookmarks",
  type: "get bookmarks"
});

export const getOrganizationLatestProviders = id => makeRequest({
  url: `/table_providers/organization_provider_latest?organization_id=${id}`,
  type: "get org_latest_providers"
});

export const getCustomCategories = () => makeRequest({
  url: "/custom_categories",
  type: "get custom_categories"
});

export const getOrganizationCustomCategories = id => makeRequest({
  url: `/custom_categories/organization_categories?organization_id=${id}`,
  type: "get org_custom_categories"
});

export const getBatchRunProviderVersionInputFile = id => makeRequest({
  url: `/batch_run_provider_versions/${id}/input_file`,
  type: "get batch_run_provider_version_input_file"
});

export const getOfflineBatchUsers = () => makeRequest({
  url: "/organization/users/offline_batch_users",
  type: "get offline_batch_users"
});

export const getBatchRunProviderVersions = () => makeRequest({
  url: "/batch_run_provider_versions",
  type:
    "get batch_run_provider_versions"
});

export const getUserNotifications = id => makeRequest({
  url: `/organization/users/${id}/notifications`,
  type: "get user_notifications"
});

export const getLatestReleaseNote = () => makeRequest({
  url: "/release_notes/latest",
  type: "get latest_release_notes"
});

export const getAllReleases = () => makeRequest({
  url: "/release_notes/all",
  type: "get relase_notes"
});

export const getMostInteractedProviders = () => makeRequest({
  url: "/table_providers/most_interacted",
  type:
    "get most interacted providers"
});

export const getNonBetaTableProvidersOutputFields = () => makeRequest({
  url: "/table_providers/output_fields",
  type:
    "get output_fields"
});

export const getTableProviderOutputFields = id => makeRequest({
  url: `/table_providers/${id}/provider_output_fields`,
  type:
    "get provider_output_fields"
});

export const getAllCurrentOrganizationRegions = () => makeRequest({
  url: "/workspaces/get_all_current_organization_regions",
  type:
    "get current_org_regions"
});

export const getAllCurrentOrganizationAwsRegions = () => makeRequest({
  url: "/workspaces/get_all_current_organization_aws_regions",
  type: "get current_org_regions"
});

export const getAllCurrentOrganizationActiveAndConfirmedUsers = () => makeRequest({
  url: "/workspaces/get_all_current_organization_active_and_confirmed_users",
  type:
    "get all_current_organization_active_and_confirmed_users"
});

export const getWorkspaceData = id => makeRequest({
  url: `/workspaces/${id}/workspace_data`,
  type: "get workspace data"
});
export const getWorkspaceDataSlim = id => makeRequest({
  url: `/workspaces/${id}/workspace_data?slim=true`,
  type: "get workspace data"
});

export const getWorkspaceBatches = id => makeRequest({
  url: `/workspaces/${id}/workspace_batches`,
  type: "get workspace batches"
});

export const getWorkspaces = () => makeRequest({
  url: "/workspaces",
  type: "get workspaces"
});

export const getEvents = ([startDate, endDate]) => makeRequest({
  url: `/events?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
  type:
    "get events"
});

export const getDataFunctions = () => makeRequest({
  url: "/organization/data_functions",
  type: "get data functions"
});

export const getOrganizationsFilteredDataFunctionList = () => makeRequest({
  url: "/organization/get_filtered_data_function_list",
  type: "get organizations filtered data function list"
});

export const getPresignedBatchUploadUrl = id => makeRequest({
  url: `/presigned_batch_upload_url?region_id=${id}`,
  type: "get presigned url"
});

export const getPresignedBatchExportUrl = id => makeRequest({
  url: `/batch_run_provider_versions/${id}/presigned_batch_export_url`,
  type:
    "get presigned export url"
});

export const getChannels = () => makeRequest({
  url: "/channels",
  type: "get channels"
});

export const getAegeanBatchRun = id => makeRequest({
  url: `/aegean_batch_runs/${id}`,
  type: "get aegean batch run"
});

export const getAtlanticConnectors = () => makeRequest({
  url: "/connectors",
  type: "get atlantic connectors"
});

export const getTableProviders = (excludeBeta = true) => makeRequest({
  url: `/table_providers/latest?exclude_beta=${excludeBeta}`,
  type: "get aegean connectors"
});

export const getTableProvider = id => makeRequest({
  url: `/table_providers/${id}/provider_latest`,
  type: "get provider latest"
});

export const getAegeanDataRegions = () => makeRequest({
  url: "/regions",
  type: "get aegean data regions"
});

export const getAegeanDataCategories = () => makeRequest({
  url: "/categories",
  type: "get aegean data categories"
});

export const getProviderTags = () => makeRequest({
  url: "/tags",
  type: "get table provider tags"
});

export const getTypes = () => makeRequest({
  url: "/types",
  type: "get types"
});

export const getPricingPlans = () => makeRequest({
  url: "/plans",
  type: "get plans",
  fetchOptions: {
    method: REQUEST_TYPE.GET,
    ...FETCH_HEADERS
  }
});

export const getExploreData = () => {
  const promises = [
    getTableProviders(),
    getAegeanDataRegions(),
    getAegeanDataCategories(),
    getProviderTags(),
    getWorkspaces(),
    getAllCurrentOrganizationActiveAndConfirmedUsers()
  ];
  return Promise.all(promises);
};

export const getDataRegions = () => makeRequest({
  url: "/data_regions",
  type: "get data regions",
  fetchOptions: {
    method: REQUEST_TYPE.GET,
    ...FETCH_HEADERS
  }
});
